import {
  Alert,
  Flex,
  Skeleton,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from '@backstage/ui';
import { useRef, useState } from 'react';

import type { GroupEntity } from '@backstage/catalog-model';
import { HistoryTabPanel } from '../TeamHistory';
import { ProjectsTable } from './ProjectsTable';
import { Separator } from 'react-aria-components';
import { TeamEmptyState } from './TeamEmptyState';
import { TeamKpiRow } from './TeamKpiRow';
import { TracksTable } from './TracksTable';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useTeamLevel } from '../../hooks/useTeamLevel';
import styles from './TeamScorecards.module.css';

export const EntityTeamScorecardsTab = () => {
  const { loading, error, team, summary } = useTeamLevel();
  const { entity } = useEntity();
  const [categoryFilters, setCategoryFilters] = useState<Set<string>>(
    new Set(),
  );
  const projectsRef = useRef<HTMLDivElement>(null);

  if (loading) {
    return (
      <Flex direction="column" gap="6" p="4" aria-busy="true">
        <Flex gap="4">
          {[0, 1, 2].map(i => (
            <Skeleton key={i} width="100%" height={120} />
          ))}
        </Flex>
        <Skeleton width="100%" height={280} />
        <Skeleton width="100%" height={360} />
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex direction="column" p="4">
        <Alert
          status="danger"
          icon
          title={`Failed to load scorecards: ${error.message}`}
        />
      </Flex>
    );
  }

  if (!team || !summary || summary.projects.length === 0) {
    return (
      <Flex direction="column" p="4">
        <TeamEmptyState team={team} />
      </Flex>
    );
  }

  const toggleCategory = (id: string) => {
    const isAdding = !categoryFilters.has(id);
    setCategoryFilters(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    if (isAdding) {
      projectsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const categoryFilterItems = summary.tracks
    .filter(c => c.id !== null && categoryFilters.has(c.id!))
    .map(c => ({ id: c.id!, name: c.name }));

  const teamDisplayName =
    (entity as GroupEntity).spec.profile?.displayName ?? entity.metadata.name;

  return (
    <Tabs aria-label={`${teamDisplayName} scorecards`}>
      <TabList>
        <Tab id="overall">Overall</Tab>
        <Tab id="history">Historical Trends</Tab>
      </TabList>
      <TabPanel id="overall">
        <Flex direction="column" gap="6" p="4">
          <TeamKpiRow projects={summary.projects} />
          <Separator className={styles.separator} />
          <TracksTable
            team={team}
            tracks={summary.tracks}
            projectsTotal={summary.projects.length}
            selectedCategoryIds={categoryFilters}
            onCategoryClick={toggleCategory}
          />
          <Separator className={styles.separator} />
          <div ref={projectsRef}>
            <ProjectsTable
              projects={summary.projects}
              categoryFilters={categoryFilters}
              categoryFilterItems={categoryFilterItems}
              onRemoveCategoryFilter={id => {
                setCategoryFilters(prev => {
                  const next = new Set(prev);
                  next.delete(id);
                  return next;
                });
              }}
              onClearAllCategoryFilters={() => setCategoryFilters(new Set())}
            />
          </div>
        </Flex>
      </TabPanel>
      <TabPanel id="history">
        <HistoryTabPanel team={team} />
      </TabPanel>
    </Tabs>
  );
};
